import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import React, { useCallback } from 'react';

import { MatrixCell } from './MatrixCell';
import {
  CELL_BUTTON_SX,
  NUMERIC_CELL_SX,
  STICKY_LABEL_CELL_SX,
} from './ServiceActivityMatrix.styles';
import { ActivityMatrixRow, MatrixFocusCell } from '../../../@types/service-activity.type';
import { ACTIVITY_STATUS_COLUMNS } from '../../../constants/service-activity.constant';
import { emptyCounts } from '../../../utils/service-activity-matrix';

export interface MatrixDataRowProps {
  row: ActivityMatrixRow;
  columnMaxima: ReturnType<typeof emptyCounts>;
  canDrillDown: boolean;
  isActive: (rowKey: string, columnIndex: number) => boolean;
  onKeyDown: (event: React.KeyboardEvent, cell: MatrixFocusCell) => void;
  registerRef: (rowKey: string, columnIndex: number) => (node: HTMLElement | null) => void;
  /** Row label click — drills to every open job for this client + service type. */
  onRowSelect: (row: ActivityMatrixRow) => void;
  /** Cell click — drills to one status group within this row. */
  onCellSelect: (row: ActivityMatrixRow, statusGroup: string) => void;
}

const LABEL_COLUMN_INDEX = -1;

const MatrixDataRowComponent: React.FC<MatrixDataRowProps> = ({
  row,
  columnMaxima,
  canDrillDown,
  isActive,
  onKeyDown,
  registerRef,
  onRowSelect,
  onCellSelect,
}) => {
  const handleRowClick = useCallback(() => onRowSelect(row), [onRowSelect, row]);
  const handleLabelKeyDown = useCallback(
    (event: React.KeyboardEvent) =>
      onKeyDown(event, { rowKey: row.key, columnIndex: LABEL_COLUMN_INDEX, interactive: true }),
    [onKeyDown, row.key]
  );

  return (
    <TableRow sx={{ '&:hover td, &:hover th': { bgcolor: 'v4.tableHover' } }}>
      <TableCell component="th" scope="row" sx={STICKY_LABEL_CELL_SX}>
        {canDrillDown ? (
          <Box
            component="button"
            type="button"
            ref={registerRef(row.key, LABEL_COLUMN_INDEX)}
            tabIndex={isActive(row.key, LABEL_COLUMN_INDEX) ? 0 : -1}
            onClick={handleRowClick}
            onKeyDown={handleLabelKeyDown}
            title={row.rowHeading || row.label}
            aria-label={`${row.rowHeading || row.label}: ${row.total.toLocaleString()} open jobs. Open the filtered job list.`}
            sx={{
              ...CELL_BUTTON_SX,
              textAlign: 'left',
              fontWeight: 500,
              px: '10px',
              py: '6px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {row.label}
          </Box>
        ) : (
          <Box
            component="span"
            sx={{ display: 'block', px: '10px', py: '6px', fontSize: '13px', fontWeight: 500 }}
          >
            {row.label}
          </Box>
        )}
      </TableCell>

      {ACTIVITY_STATUS_COLUMNS.map((column, index) => (
        <MatrixCell
          key={column.field}
          column={column}
          value={row.counts[column.field]}
          columnMax={columnMaxima[column.field]}
          rowLabel={row.rowHeading || row.label}
          focus={{ rowKey: row.key, columnIndex: index, interactive: row.counts[column.field] > 0 }}
          isActive={isActive(row.key, index)}
          canDrillDown={canDrillDown}
          onSelect={(statusGroup) => onCellSelect(row, statusGroup)}
          onKeyDown={onKeyDown}
          registerRef={registerRef(row.key, index)}
        />
      ))}

      <TableCell sx={{ ...NUMERIC_CELL_SX, bgcolor: 'v4.tableEven' }}>
        <Box
          component="span"
          sx={{ display: 'block', py: '6px', fontSize: '13px', fontWeight: 700 }}
        >
          {row.total.toLocaleString()}
        </Box>
      </TableCell>
    </TableRow>
  );
};

/** Memoised: when only the sort changes, unchanged row objects are reused by
 *  reference and their subtrees skip re-rendering. */
export const MatrixDataRow = React.memo(MatrixDataRowComponent);
